import { Accordion, AccordionVariant } from '@aus-platform/design-system';
import React, { useMemo, useState } from 'react';
import { SectionHeader } from 'shared/components';
import { useDashboardCategoryConfig } from 'shared/hooks';
import { type DashboardCategoryConfig } from 'src/map-3d/components';
import { DashboardConfigurationAccordion } from './dashboard-configuration-accordion';
import {
  type DashboardConfigurationsEditProps,
  type DashboardField,
} from './types';

export const DashboardConfigurationsEdit: React.FC<
  DashboardConfigurationsEditProps
> = ({ onNavigateBack }) => {
  // Hooks.
  const { dashboardCategoryConfig } = useDashboardCategoryConfig();

  // Memo.
  const dashboardFields = useMemo<DashboardField[]>(
    () =>
      dashboardCategoryConfig.map(
        (categoryConfig: DashboardCategoryConfig) => ({
          key: categoryConfig.categoryKey,
          name: categoryConfig.name,
          entries: categoryConfig.options.map((option) => ({
            id: option.value.id,
            name: option.label,
            isActive: option.value.isActive,
            category: option.value.category,
          })),
        }),
      ),
    [dashboardCategoryConfig],
  );

  // States.
  const [activeAccordionKey, setActiveAccordionKey] = useState<
    string | undefined
  >(dashboardFields[0]?.key);

  // Handlers.
  const toggleAccordion = (accordionKey: string) => {
    setActiveAccordionKey((currentKey) =>
      currentKey === accordionKey ? undefined : accordionKey,
    );
  };

  // Renderers.
  return (
    <div className="settings-content">
      <div className="settings-content__body dashboard-configuration">
        <SectionHeader
          backLabel="Dashboard Configurations"
          description="All the custom entries set up for your org."
          onBack={onNavigateBack}
        />

        <div className="dashboard-configuration__accordions">
          <Accordion
            activeKey={activeAccordionKey}
            variant={AccordionVariant.ChevronRight}
          >
            {dashboardFields.map((categorySection: DashboardField) => (
              <DashboardConfigurationAccordion
                key={categorySection.key}
                fieldKey={categorySection.key}
                title={categorySection.name}
                entries={categorySection.entries}
                isAccordionActive={activeAccordionKey === categorySection.key}
                onToggleAccordionItem={() =>
                  toggleAccordion(categorySection.key)
                }
              />
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
};
